import React, { useState } from 'react';
import Layout from '../../components/Layout';
import SEO from '../../components/SEO';
import Container from '../../components/Container';
import { Desktop, Tablet } from '../../constants/responsive';
import { css } from '@emotion/core';
import HomeWhy from '../../components/HomeWhy';
import GradientBackground from '../../components/GradientBackground';
import SGUDetailsBanner from '../../components/SGUDetailsBanner';
import SGUIcon from '../../components/SGUDetailsIcon';
import Blank from '../../components/Blank';
import SGUBenefit from '../../components/SGUDetailsBenefits';
import { graphql, useStaticQuery } from 'gatsby';
import SGUQuestion from '../../components/SGUDetailsQuestion';
import InvestmentOptions from '../../components/InvestmentOptions';
import { useIntl } from 'react-intl';
import {
  SGUBackTestingPic,
  SGUSelectRiskType,
  SGUPerformanceDetails,
} from '../../components/SGUBackTestingPic';
import { FormattedHTMLMessage, Link } from 'gatsby-plugin-intl';

const Title = ({ children, ...rest }) => {
  return (
    <h2
      css={css`
        margin: 0;
        font-weight: 500;
        font-size: 24px;
        line-height: 33px;
        ${Tablet} {
          font-size: 28px;
          line-height: 38px;
        }
      `}
      {...rest}
    >
      {children}
    </h2>
  );
};

const SmartGlobalU = () => {
  const intl = useIntl();
  const data = useStaticQuery(graphql`
    query {
      AllWeather: file(
        relativePath: { eq: "asset-allocation/SGU/Benefits/all-weather.png" }
      ) {
        childImageSharp {
          fluid(maxWidth: 400) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
      Diverse: file(
        relativePath: { eq: "asset-allocation/SGU/Benefits/diverse.png" }
      ) {
        childImageSharp {
          fluid(maxWidth: 400) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
      Manage: file(
        relativePath: { eq: "asset-allocation/SGU/Benefits/manage.png" }
      ) {
        childImageSharp {
          fluid(maxWidth: 400) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }

      allProduct {
        edges {
          node {
            product_id
            riskType
            locale
            data {
              pid
              algo_type_id
              market
              category
              edition
              currency
              product_name
              product_logo
              min_investment_amount
              tags
              tags_detail_page
              backtesting {
                date
                value
              }
              benchmark {
                date
                value
              }
              description_card_1 {
                description
                prefix_1
                metric_1
                sub_title
                title
                value_1
              }
              description_card_2 {
                description
                sub_title
                title
                factors {
                  content
                  image
                  image_official
                  name
                }
              }
              description_card_3 {
                description
                metric_1
                metric_2
                prefix_1
                prefix_2
                sub_title
                title
                value_1
                value_2
              }
            }
          }
        }
      }
    }
  `);

  const [yearlyReturn, setYearlyReturn] = useState(0); // 累计收益率
  const [remarks, setRemarks] = useState(0); // 基准
  const [maxDrawDown, setMaxDrawDown] = useState(0); // 最大回撤
  const [riskType, setRiskType] = useState(3);
  const product = data.allProduct.edges
    .map((node) => node.node)
    .map((product) => {
      return {
        ...product,
        factors: product.data.description_card_2.factors,
      };
    })
    .filter((product) => product.locale === intl.locale)
    .filter((product) => {
      return product.data.pid === 56 && product.riskType === riskType;
    })[0];

  return (
    <Layout footerForm footerBand={false}>
      <SEO
        title={intl.formatMessage({
          id: 'productDetails.pid-56.SEO.title',
        })}
        description={intl.formatMessage({
          id: 'productDetails.pid-56.SEO.description',
        })}
        keywords={intl.formatMessage({
          id: 'productDetails.pid-56.SEO.keywords',
        })}
      />

      <div
        className='SGU-page-content'
        css={css`
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
        `}
      >
        <SGUDetailsBanner />
        <Container>
          {/* At a Glance */}
          <Blank height={[52, 100, 100]} />
          <div>
            <h2
              css={css`
                width: 100%;
                margin: 0;
                font-weight: 500;
                text-align: left;
                font-size: 24px;
                line-height: 33px;
                br {
                  display: block;
                }
                ${Tablet} {
                  br {
                    display: none;
                  }
                  text-align: center;
                  font-size: 28px;
                  line-height: 38px;
                }
              `}
            >
              <FormattedHTMLMessage
                id={`productDetails.pid-56.at-a-glance.title`}
              />
            </h2>
            <Blank height={[20, 30, 50]} />
            <div
              css={css`
                display: flex;
                flex-wrap: wrap;
                ${Tablet} {
                  justify-content: flex-end;
                }
                ${Desktop} {
                  justify-content: center;
                }
              `}
            >
              <SGUIcon name='min-investment' />
              <Blank width={[0, 100, 0]} />
              <SGUIcon name='coverage' />
              <SGUIcon name='performance' />
              <Blank width={[0, 100, 0]} />
              <SGUIcon name='max-drawdown' />
            </div>
            <Blank height={[0, 0, 20]} />
            {/* 提示小字 */}
            <div
              css={css`
                text-align: left;
                color: #999999;
                font-size: 10px;
                line-height: 14px;
                ${Tablet} {
                  text-align: center;
                  font-size: 10px;
                  line-height: 14px;
                  padding: 0 40px;
                }
                ${Desktop} {
                  padding: 0;
                  font-size: 12px;
                  line-height: 16px;
                }
              `}
            >
              {intl.formatMessage({
                id: `productDetails.pid-56.at-a-glance.text-1`,
              })}
              <br />
              {intl.formatMessage({
                id: `productDetails.pid-56.at-a-glance.text-2`,
              })}
            </div>
          </div>
        </Container>
        {/* past performance */}
        <Blank height={[100, 60, 81]} />
        <Container
          css={css`
            ${Desktop} {
              padding: 0;
            }
          `}
        >
          <Title>
            {intl.formatMessage({
              id: `productDetails.pid-56.past-performance.title`,
            })}
          </Title>
          <Blank height={[10, 30, 20]} />
          <div
            css={css`
              display: flex;
              flex-direction: column-reverse;

              ${Desktop} {
                flex-direction: row;
              }
            `}
          >
            {/* 左-回测图 */}
            <SGUBackTestingPic
              showLeft={'true'}
              productName={product.data.product_name}
              benchmarkName={`productDetails.pid-56.benchmark-name`}
              combinationName={'pid-56'}
              setRemarks={setRemarks}
              setYearlyReturn={setYearlyReturn}
              setMaxDrawDown={setMaxDrawDown}
              riskType={riskType}
              product={product}
            />
            <Blank height={[20, 11, 0]} />
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: center;
                ${Tablet} {
                  display: flex;
                  flex-direction: row;
                  align-items: stretch;
                }
                ${Desktop} {
                  display: block;
                  width: 390px;
                }
              `}
            >
              <SGUSelectRiskType setRiskType={setRiskType} />
              <Blank height={[0, 0, 16]} />
              <SGUPerformanceDetails
                yearlyReturn={yearlyReturn}
                remarks={remarks}
                maxDrawDown={maxDrawDown}
              />
            </div>
          </div>
        </Container>
        <Blank height={[150, 150, 150]} />
        {/* algorithms */}
        <HomeWhy
          title={intl.formatMessage({
            id: 'productDetails.pid-56.algorithms.title',
          })}
          nameList={['beta', 'rotation', 'hedging']}
          namePrePath={'productDetails.pid-56.algorithms'}
          titleStyle={css`
            font-weight: 500;
            font-size: 24px;
            line-height: 33px;
            padding: 0 20px;
            ${Tablet} {
              padding: 0;
              font-size: 28px;
              line-height: 38px;
            }
            ${Desktop} {
              font-size: 28px;
              line-height: 38px;
            }
          `}
          mobileScroll
        />
        <GradientBackground
          height={[0, 258, 258]}
          background={`linear-gradient(180deg, rgba(242, 242, 242, 0) 0%, #F2F2F2 100%)`}
          marginTop={[0, -103, -50]}
          css={css`
            z-index: -1;
          `}
        />
        <Blank height={[60, 0, 0]} />
        {/* 3 Major Benefits */}
        <Container
          css={css`
            ${Tablet} {
              padding: 0 34px;
            }
            ${Desktop} {
              padding: 0 20px;
              margin-top: -58px;
            }
          `}
        >
          <h2
            css={css`
              margin: 0;
              font-weight: 500;
              font-size: 24px;
              line-height: 33px;
              text-align: center;
              ${Tablet} {
                text-align: left;
                font-size: 28px;
                line-height: 38px;
              }
              ${Desktop} {
                font-size: 28px;
                line-height: 38px;
              }
            `}
          >
            <FormattedHTMLMessage
              id={`productDetails.pid-56.three-major-benefits.title`}
            />
          </h2>
          <Blank height={[30, 50, 25]} />
          <SGUBenefit
            imgUrl={data[`AllWeather`].childImageSharp.fluid.src}
            name='all-weather'
            imgWidth={[196, 204, 487]}
            imgHeight={[153, 160, 382]}
          />
          <SGUBenefit
            imgUrl={data[`Manage`].childImageSharp.fluid.src}
            name='manage'
            imgWidth={[302, 288, 532]}
            imgHeight={[162, 288, 532]}
            css={css`
              margin-top: 50px;
              ${Tablet} {
                flex-direction: row-reverse;
                margin-top: 16px;
              }
              ${Desktop} {
                margin-top: -72px;
              }
            `}
          />
          <SGUBenefit
            imgUrl={data[`Diverse`].childImageSharp.fluid.src}
            name='diverse'
            imgWidth={[212, 212, 382]}
            imgHeight={[212, 212, 382]}
            css={css`
              margin-top: 50px;
              ${Tablet} {
                margin-top: -35px;
              }
              ${Desktop} {
                margin-top: -23px;
              }
            `}
          />
        </Container>
        <GradientBackground
          height={[258, 0, 0]}
          background={`linear-gradient(180deg, rgba(242, 242, 242, 0) 0%, #F2F2F2 100%)`}
          marginTop={[-1114, 0, 0]}
          css={css`
            z-index: -1;
          `}
        />
        <GradientBackground
          height={[937, 0, 0]}
          background={`#F2F2F2`}
          marginTop={[0, 0, 0]}
          css={css`
            z-index: -1;
          `}
        />
        <Blank height={[37, 150, 78]} />
        {/* suitable */}
        <HomeWhy
          title={intl.formatMessage({
            id: 'productDetails.pid-56.suitable-for.title',
          })}
          nameList={['tolerance', 'returns', 'experience']}
          namePrePath={'productDetails.pid-56.suitable-for'}
          titleStyle={css`
            font-weight: 500;
            font-size: 24px;
            line-height: 33px;
            padding: 0 20px;
            ${Tablet} {
              padding: 0;
              font-size: 28px;
              line-height: 38px;
            }
            ${Desktop} {
              font-size: 28px;
              line-height: 38px;
            }
          `}
          mobileScroll
        />
        <GradientBackground
          height={[0, 1413, 1672]}
          background={`#F2F2F2`}
          marginTop={[0, -1300, -1600]}
          css={css`
            z-index: -1;
          `}
        />
        <Container
          css={css`
            ${Tablet} {
              padding: 0 34px;
            }
            ${Desktop} {
              padding: 0 20px;
            }
          `}
        >
          {/* questions */}
          <Blank height={[60, 37, 78]} />
          <div
            css={css`
              ${Tablet} {
              }
              ${Desktop} {
                display: flex;
              }
            `}
          >
            <h3
              css={css`
                margin: 0;
                font-weight: 500;
                font-size: 24px;
                line-height: 33px;
                ${Tablet} {
                  font-size: 28px;
                  line-height: 38px;
                  padding-left: ;
                }
                ${Desktop} {
                  width: 264px;
                  font-size: 36px;
                  line-height: 49px;
                }
              `}
            >
              <strong>
                {intl.formatMessage({
                  id: `productDetails.pid-56.questions.title`,
                })}
              </strong>
            </h3>
            <Blank height={[10, 30, 0]} />
            <div
              css={css`
                a,
                a::active,
                a:visited {
                  color: #ee2f79;
                }
                ${Desktop} {
                  width: 903px;
                }
              `}
            >
              <SGUQuestion name='Q1' />
              <SGUQuestion name='Q2' />
              <SGUQuestion name='Q3' />
              <SGUQuestion name='Q4' />
              <Link
                to='/resources/faq'
                css={css`
                  :active,
                  :visited {
                    color: #ee2f79;
                  }
                  display: block;
                  color: #ee2f79;
                  margin-top: -6px;
                  padding-left: 10px;
                  font-size: 12px;
                  line-height: 30px;
                  ${Tablet} {
                    margin-top: 0px;
                    padding-left: 20px;
                    font-size: 18px;
                    line-height: 30px;
                  }
                  ${Desktop} {
                    font-size: 20px;
                    line-height: 30px;
                  }
                `}
              >
                {intl.formatMessage({
                  id: `productDetails.pid-56.questions.view-more.text`,
                })}
              </Link>
            </div>
          </div>
        </Container>
        <Blank height={[100, 157, 152]} />
        {/* other investment options */}
        <Container
          css={css`
            overflow: normal;
          `}
        >
          <div
            css={css`
              color: #161616;
              font-size: 24px;
              font-weight: 500;
              line-height: 1.33;
              margin-top: 0px;
              ${Tablet} {
                font-size: 36px;
                font-weight: 500;
                line-height: 1.33;
                margin-top: 0px;
                width: 512px;
              }
              ${Desktop} {
                font-size: 36px;
                font-weight: bold;
                line-height: normal;
                margin-top: 0;
                width: 100%;
              }
            `}
          >
            {intl.formatMessage({
              id: 'individuals.investment-options.other-title',
            })}
          </div>
          <Blank height={[10, 40, 49]} />
        </Container>
        <div
          css={css`
            width: 100%;
            margin-top: 0px;
            ${Tablet} {
              max-width: 970px;
              margin-top: 0px;
            }
            ${Desktop} {
              padding-left: 117px;
              max-width: 1440px;
              margin-top: 0;
            }
          `}
        >
          <InvestmentOptions doNotShow='LongTerm' />
        </div>
        <Blank height={[100, 100, 100]} />
      </div>
      {/* 因为页面内有两种背景色且高度不规律，所以本页面的背景颜色使用背景色组件GradientBackground，
   通过调节不同组件实例的z-index值，实现指定区域展示指定背景色。 */}
      <GradientBackground
        height={[4985, 4845, 4919]}
        background={`#F8F8F8`}
        marginTop={[-4985, -4845, -4919]}
        css={css`
          z-index: -2;
        `}
      />
    </Layout>
  );
};

export default SmartGlobalU;
